import { useState } from "react";
import { toast } from "sonner";
import { adminOutreachPitch, adminOutreachPitchPreview, adminOutreachUpsell, adminOutreachUpsellPreview, adminDigestPreview, adminDigestSend } from "../lib/api";

const d10 = (x) => String(x || "").slice(0, 10);

function FollowUp({ f, slug, kind }) {
  if (!f) return null;
  return (
    <span className="text-[#666]" data-testid={`${kind}-followup-${slug}`}>
      {" · "}{f.opened_dashboard_at ? `opened dashboard ${d10(f.opened_dashboard_at)}` : "dashboard not opened yet"}
      {kind === "pitch" && (f.bought?.count ? <b className="text-black"> · bought {f.bought.count} slot{f.bought.count > 1 ? "s" : ""} (${f.bought.usd})</b> : " · no slot bought yet")}
      {kind === "upsell" && (f.upgraded_at ? <b className="text-black"> · upgraded to Starter {d10(f.upgraded_at)}</b> : " · not upgraded yet")}
    </span>
  );
}

function EmailAction({ t, kind, label, previewFn, sendFn, onChanged }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const doPreview = async () => {
    try {
      setPreview(await previewFn(t.slug, { note: note || null }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "could not render preview");
    }
  };
  const send = async () => {
    if (!window.confirm(`send the ${label} to ${t.claimed_by}?`)) return;
    setBusy(true);
    try {
      await sendFn(t.slug, { note: note || null });
      toast.success(`${label} sent to ${t.claimed_by}`);
      setOpen(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "send failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" className="tv-link underline" onClick={() => setOpen((o) => !o)} data-testid={`${kind}-toggle-${t.slug}`}>{open ? "close" : `send ${label}`}</button>
      {open && (
        <div className="mt-2 border border-[#999] p-2 space-y-2 basis-full" data-testid={`${kind}-form-${t.slug}`}>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={600}
            placeholder="optional opening line (replaces the default first sentence)" className="border border-black p-1 text-[12px] font-mono w-full" data-testid={`${kind}-note-${t.slug}`} />
          <div className="flex gap-2">
            <button type="button" className="tv-btn" onClick={doPreview} data-testid={`${kind}-preview-${t.slug}`}>[preview]</button>
            <button type="button" className="tv-btn tv-btn-primary" disabled={busy} onClick={send} data-testid={`${kind}-send-${t.slug}`}>{busy ? "sending…" : `[send to ${t.claimed_by}]`}</button>
          </div>
          {preview && (
            <div data-testid={`${kind}-preview-body-${t.slug}`}>
              <div className="text-[#666]">to: {preview.to} · subject: <b className="text-black">{preview.subject}</b></div>
              <pre className="whitespace-pre-wrap text-[11px] border border-[#ddd] p-2 mt-1 bg-[#fafafa]">{preview.text}</pre>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function DigestBox() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = async () => {
    try {
      setP(await adminDigestPreview());
    } catch {
      toast.error("could not build digest");
    }
  };
  const send = async () => {
    setBusy(true);
    try {
      const r = await adminDigestSend();
      toast.success(`digest sent to ${r.to}`);
      preview();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "send failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section data-testid="digest-section">
      <h2 className="text-[15px] font-bold border-b border-black pb-1">weekly digest</h2>
      <p className="text-[#444] mt-2">Every Monday 9:00 am US Eastern: new claims, invites sent/opened, reminders due, slots sold, Starter starts and Stripe revenue for the past 7 days.</p>
      <div className="flex flex-wrap gap-2 items-center mt-2">
        <button type="button" className="tv-btn" onClick={preview} data-testid="digest-preview">[preview this week]</button>
        <button type="button" className="tv-btn tv-btn-primary" disabled={busy} onClick={send} data-testid="digest-send">{busy ? "sending…" : "[send now]"}</button>
        {p?.last_sent && <span className="text-[#666]" data-testid="digest-last-sent">last sent {d10(p.last_sent.sent_at)} → {p.last_sent.to}</span>}
      </div>
      {p && (
        <div className="mt-2" data-testid="digest-preview-body">
          <div className="text-[#666]">to: {p.to} · subject: <b className="text-black">{p.subject}</b></div>
          <pre className="whitespace-pre-wrap text-[11px] border border-[#ddd] p-2 mt-1 bg-[#fafafa]">{p.text}</pre>
        </div>
      )}
    </section>
  );
}

function PitchRow({ t, viewDays, onChanged }) {
  return (
    <li className="border-b border-[#ddd] py-2" data-testid={`pitch-row-${t.slug}`}>
      <div className="flex flex-wrap gap-x-3 items-baseline">
        <a href={`/tool/${t.slug}`} className="tv-link font-bold" target="_blank" rel="noreferrer">{t.title}</a>
        <span className="text-[#666]">{t.claimed_by} · {t.plan} · {t.views_30d} views/{viewDays}d · on {t.pages} pages · {t.open_slots} open slots</span>
      </div>
      <div className="flex flex-wrap gap-x-3 items-baseline pl-4">
        <span data-testid={`pitch-status-${t.slug}`} className={t.pitch ? "font-bold" : "text-[#b91c1c]"}>{t.pitch ? `pitched ${d10(t.pitch.sent_at)}` : "not pitched"}</span>
        <FollowUp f={t.pitch} slug={t.slug} kind="pitch" />
        <EmailAction t={t} kind="pitch" label="sponsor pitch" previewFn={adminOutreachPitchPreview} sendFn={adminOutreachPitch} onChanged={onChanged} />
      </div>
      {t.plan === "free" && (
        <div className="flex flex-wrap gap-x-3 items-baseline pl-4">
          <span data-testid={`upsell-status-${t.slug}`} className={t.upsell ? "font-bold" : "text-[#b91c1c]"}>{t.upsell ? `Starter offer ${d10(t.upsell.sent_at)}` : "no Starter offer yet"}</span>
          <FollowUp f={t.upsell} slug={t.slug} kind="upsell" />
          <EmailAction t={t} kind="upsell" label="Starter offer" previewFn={adminOutreachUpsellPreview} sendFn={adminOutreachUpsell} onChanged={onChanged} />
        </div>
      )}
    </li>
  );
}

export default function PitchSection({ claimed, viewDays, onChanged }) {
  return (
    <section data-testid="pitch-section">
      <h2 className="text-[15px] font-bold border-b border-black pb-1">claimed vendors — sponsor pitch ({claimed.length})</h2>
      <p className="text-[#444] mt-2">
        Emails the owner a rate card for every /best and /alternatives page their tool is on (price per month and per year, open slots — no traffic numbers) with a
        &quot;book a slot&quot; button into their vendor dashboard. Free-tier owners can also get a one-click Starter offer at the founding price
        ($49/mo or $495/yr first year). Claimed owners can buy even while the public switches are off. Dashboard opens and purchases after each email are tracked.
      </p>
      <ul className="mt-2" data-testid="pitch-list">
        {claimed.map((t) => <PitchRow key={t.slug} t={t} viewDays={viewDays} onChanged={onChanged} />)}
        {claimed.length === 0 && <li className="text-[#888]">no claimed listings yet.</li>}
      </ul>
      <div className="mt-8"><DigestBox /></div>
    </section>
  );
}
